


















































































































































import { Component, Inject, Mixins, Watch } from "vue-property-decorator";
import { ActionTypes } from "@/data/enums";
import CountriesStore from "@/mixins/constants/countriesStore";

import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import { DataModules } from "@/store/modules/data";
import { mapState } from "vuex";
import RequestPromises from "@/mixins/requestPromises";
import { ICompany } from "@/models/companies";
import { IAddress } from "@/models/addresses";

@Component({
  components: {
    "address-search": () =>
      import("@/components/app/global/address/addressSearch.vue")
  },
  computed: {
    ...mapState({
      clientAddressesScope(state, getters) {
        return getters[`data/${DataModules.CLIENTS_ADDRESSES}/clientScope`](
          this.basketData.clientId
        );
      },
      clientAddresses(state, getters) {
        return this.$_.orderBy(
          getters[`data/listArray`]({
            storeModule: DataModules.CLIENTS_ADDRESSES,
            scope: this.clientAddressesScope
          }),
          ["created_at", "asc"]
        );
      },
      clientCompaniesScope(state, getters) {
        return getters[`data/${DataModules.CLIENTS_COMPANIES}/clientScope`](
          this.basketData.clientId
        );
      },
      clientCompanies(state, getters) {
        return this.$_.orderBy(
          getters[`data/listArray`]({
            storeModule: DataModules.CLIENTS_COMPANIES,
            scope: this.clientCompaniesScope
          }),
          ["created_at", "asc"]
        );
      }
    })
  }
})
export default class BillableEntities extends Mixins(
  RequestPromises,
  CountriesStore
) {
  // Inject

  @Inject({ from: "basketProviderData" })
  public readonly $basketData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  // Data

  public actionTypes = ActionTypes;
  public addingAddress = false;
  public addressId = this.basketAddressId || "";
  public addressSearchedKey = this.$uuid();
  public clientAddresses!: IAddress[];
  public clientAddressesScope!: string;
  public clientCompanies!: ICompany[];
  public clientCompaniesScope!: string;
  public companyId: ICompany["id"] = this.basketCompanyId || "";
  public isLoading = true;
  public isProcessing = false;
  public searchedValue = "";
  public selectedObjectId: string | null = this.basketCompanyId
    ? `company_${this.basketCompanyId}`
    : this.basketAddressId
    ? `address_${this.basketAddressId}`
    : null;

  // Computed

  public get basketData() {
    return this.$basketData();
  }

  public get basketAddressId() {
    return this.basketData.basket?.address_id;
  }

  public get basketCompanyId() {
    return this.basketData.basket?.company_id;
  }

  // Life-cycle methods

  public async created() {
    this.loadEntities();
  }

  public beforeDestroy() {
    Promise.all([
      // Bin client addresses
      this.$store.dispatch("data/binList", {
        storeModule: DataModules.CLIENTS_ADDRESSES,
        scope: this.clientAddressesScope,
        vm: this
      }),
      // Bin client companies
      this.$store.dispatch("data/binList", {
        storeModule: DataModules.CLIENTS_COMPANIES,
        scope: this.clientCompaniesScope,
        vm: this
      })
    ]);
  }

  // Methods

  public async loadEntities() {
    try {
      this.isLoading = true;
      await Promise.all([this.getClientAddresses(), this.getClientCompanies()]);
    } finally {
      this.isLoading = false;
    }
  }

  public getClientAddresses() {
    return this.$store.dispatch(`data/${DataModules.CLIENTS_ADDRESSES}/list`, {
      id: this.basketData.client?.id || this.basketData.clientId,
      scope: this.clientAddressesScope,
      ignoreStored: true,
      vm: this
    });
  }

  public async selectClientAddress(address: IAddress) {
    try {
      this.isProcessing = true;
      if (this.selectedObjectId === `address_${address.id}`) return;
      this.selectedObjectId = `address_${address.id}`;
      await this.$basketMethods.updateBasketData({
        address_id: address.id,
        company_id: null
      });
      // Reload basket incase tax jurisdiction changed
      await this.$basketMethods.getBasket(true);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public deleteClientAddress(address: IAddress) {
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_ADDRESSES}/confirmDelete`,
      {
        address,
        onConfirm: () => this.loadEntities()
      }
    );
  }

  public async getClientCompanies() {
    return this.$store.dispatch(`data/${DataModules.CLIENTS_COMPANIES}/list`, {
      id: this.basketData.client?.id || this.basketData.clientId,
      scope: this.clientCompaniesScope,
      ignoreStored: true,
      vm: this
    });
  }

  public async selectClientCompany(company: ICompany) {
    try {
      this.isProcessing = true;
      if (this.selectedObjectId === `company_${company.id}`) return;
      this.selectedObjectId = `company_${company.id}`;
      await this.$basketMethods.updateBasketData({
        address_id: company.address_id,
        company_id: company.id
      });
      // Reload basket incase tax jurisdiction changed
      await this.$basketMethods.getBasket(true);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public deleteClientCompany(company: ICompany) {
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_COMPANIES}/confirmDelete`,
      {
        company,
        onConfirm: () => this.loadEntities()
      }
    );
  }

  public async onAddressSearchResult(address, searchedValue) {
    this.openAddAddressModal({
      address: {
        ...address,
        name: address.address_1,
        type: 1,
        default: true
      },
      actionType: ActionTypes.CREATE,
      searchedValue
    });
  }

  public addNewAddress() {
    return this.openAddAddressModal({ actionType: ActionTypes.CREATE });
  }

  public addNewCompany() {
    return this.openAddCompanyModal({ actionType: ActionTypes.CREATE });
  }

  public openAddAddressModal({
    address = {} as IAddress,
    actionType = ActionTypes.UPDATE,
    searchedValue = ""
  }: {
    address?: IAddress;
    actionType?: ActionTypes;
    searchedValue?: string;
  }) {
    this.addingAddress = true;
    this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import(
            "@/components/app/global/client/addEditClientAddressModal.vue"
          ),
        props: this.$_.merge(
          { clientId: this.basketData.clientId },
          this.$_.isEmpty(address)
            ? {}
            : {
                data: this.$_.pick(address, [
                  "name",
                  "address_1",
                  "address_2",
                  "country_id",
                  "region_id",
                  "city",
                  "postcode",
                  "type"
                ]),
                actionType,
                addressId:
                  actionType === ActionTypes.UPDATE ? address.id : undefined
              },
          searchedValue ? { searchedValue } : {}
        ),
        width: 640,
        onCancel: () => {
          this.clearAddressSearch();
        },
        events: {
          success: async (address: IAddress) => {
            // Reload entities
            await this.loadEntities();
            if (actionType === ActionTypes.CREATE) {
              // Auto-select newly created record
              await this.selectClientAddress(address);
            } else if (this.basketAddressId === address.id) {
              // Reload basket incase tax jurisdiction changed
              await this.$basketMethods.getBasket(true);
            }
            this.clearAddressSearch();
          }
        }
      }
    });
  }

  public openAddCompanyModal({
    company = {} as ICompany,
    actionType = ActionTypes.UPDATE
  }: {
    company?: ICompany;
    actionType?: ActionTypes;
  }) {
    this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import(
            "@/components/app/global/client/addEditClientCompanyModal.vue"
          ),
        props: {
          clientId: this.basketData.clientId,
          ...(this.$_.isEmpty(company)
            ? {}
            : {
                data: this.$_.pick(company, [
                  "name",
                  "vat_number",
                  "reg_number",
                  "address_id",
                  "email_id",
                  "phone_id"
                ]),
                actionType,
                companyId:
                  actionType === ActionTypes.UPDATE ? company.id : undefined
              })
        },
        width: 640,
        events: {
          success: async (company: ICompany) => {
            // Reload entities
            await this.loadEntities();
            if (actionType === ActionTypes.CREATE) {
              // Auto-select newly created record
              await this.selectClientCompany(company);
            } else if (this.basketCompanyId === company.id) {
              // Reload basket incase tax jurisdiction changed
              await this.$basketMethods.getBasket(true);
            }
          }
        }
      }
    });
  }

  public clearAddressSearch() {
    this.addingAddress = false;
    this.$set(this, "searchedValue", "");
    this.addressSearchedKey = this.$uuid();
  }

  @Watch("clientAddresses.length")
  public onAddressesChange(count, oldCount) {
    if (!this.selectedObjectId && !oldCount) {
      // Select default address
      this.addressId = this.$_.get(
        this.$_.find(this.clientAddresses, ["default", true]),
        "id",
        ""
      );
    }
  }
}
